import { useEffect, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { Separator } from '@/components/ui/separator';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from './app-sidebar';
import { CommandSearch } from './command-search';
import { navTargets, type NavItem } from './navigation';
import { useRouteTitle } from './route-title';
import { ShellBreadcrumb } from './shell-breadcrumb';
import { UserMenu } from './user-menu';

/**
 * Estado da barra lembrado entre visitas (o cookie é gravado pelo próprio `SidebarProvider`).
 * Sem preferência, a largura decide: aberta no desktop, só ícones no tablet (§19).
 */
function initialSidebarOpen(): boolean {
  const saved = document.cookie.match(/(?:^|; )sidebar_state=(true|false)/)?.[1];
  if (saved) return saved === 'true';
  return window.matchMedia('(min-width: 64rem)').matches;
}

/**
 * Moldura comum ao sistema da empresa e ao painel da plataforma (§19): barra lateral, cabeçalho
 * com trilha, busca e usuário, e o conteúdo. Cada shell só diz quais módulos e qual contexto.
 */
export function AppLayout({
  home,
  items,
  context,
  accountPath,
  banner,
  headerActions,
}: {
  home: string;
  items: NavItem[];
  context: ReactNode;
  accountPath: string;
  /** Aviso fixo acima de todas as páginas (ex.: política de tarifa para revisar). */
  banner?: ReactNode;
  /** Ações do cabeçalho de todas as telas (ex.: o "Pergunte ao troig" da empresa). */
  headerActions?: ReactNode;
}) {
  const title = useRouteTitle();
  useEffect(() => {
    document.title = title ? `${title} · troig` : 'troig';
  }, [title]);

  return (
    <SidebarProvider defaultOpen={initialSidebarOpen()}>
      <AppSidebar home={home} items={items} context={context} />
      <SidebarInset className="min-w-0 bg-app-canvas">
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b bg-background px-4">
          <SidebarTrigger aria-label="Abrir ou recolher o menu" className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <div className="min-w-0 flex-1">
            <ShellBreadcrumb targets={navTargets(items)} title={title} />
          </div>
          {headerActions}
          <CommandSearch items={items} />
          <UserMenu accountPath={accountPath} />
        </header>
        {banner}
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  );
}
